// src/pages/Help/QuickStart/index.js
import React from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, CardBody, Button } from "reactstrap";

// Import images (you'll need to add these to your assets)
import transcriptEntryImg from "../../assets/images/eirna/quickstart/quickstart_transcript_entry.png";
import annotatedSubcodonImg from "../../assets/images/eirna/quickstart/quickstart_annotated_subcodon.png";
import orfTranslationTableImg from "../../assets/images/eirna/quickstart/quickstart_orf_translation_table.png";
import dataQualityPanelImg from "../../assets/images/eirna/quickstart/quickstart_data_quality_panel.png";
import deFileSelectionImg from "../../assets/images/eirna/quickstart/quickstart_DE_file_selection.png";
import deFileSelectionRnaseqImg from "../../assets/images/eirna/quickstart/quickstart_DE_file_selection_rnaseq.png";
import dePlotImg from "../../assets/images/eirna/quickstart/quickstart_DE_plot.png";

const QuickStart = () => {
  const navigate = useNavigate();

  const handleBackToHelp = () => {
    navigate("/help");
  };

  return (
    <div className="page-content">
      <Container fluid>
        {/* Header */}
        <Row className="mb-4">
          <Col lg={12}>
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <Button
                  color="light"
                  size="sm"
                  onClick={handleBackToHelp}
                  style={{
                    marginRight: "16px",
                    border: "1px solid #d1d5db",
                    color: "#6b7280",
                  }}
                >
                  <i className="ri-arrow-left-line me-1"></i>
                  Back to Help
                </Button>
                <h4 className="mb-0 fw-bold" style={{ color: "#2F9E37" }}>
                  Quickstart Help Page
                </h4>
              </div>
            </div>
          </Col>
        </Row>

        {/* Main Content */}
        <Row>
          <Col lg={12}>
            <Card
              style={{
                border: "none",
                borderRadius: "4px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                backgroundColor: "#ffffff",
              }}
            >
              <CardBody style={{ padding: "40px" }}>
                {/* Introduction */}
                <div className="mb-5">
                  <p
                    style={{
                      fontSize: "16px",
                      lineHeight: "1.6",
                      color: "#374151",
                      marginBottom: "0",
                    }}
                  >
                    This is a basic image based walkthrough of the some of main
                    features of EIRNABio Connect using an example dataset.
                  </p>
                </div>

                {/* Section 1: Visualising an individual gene/transcript */}
                <div className="mb-5">
                  <h5
                    className="mb-4 fw-bold"
                    style={{
                      color: "#2F9E37",
                      fontSize: "20px",
                      borderBottom: "2px solid #2F9E37",
                      paddingBottom: "8px",
                    }}
                  >
                    Visualising an individual gene/transcript
                  </h5>

                  <div className="mb-4">
                    <p
                      style={{
                        fontSize: "15px",
                        lineHeight: "1.6",
                        color: "#374151",
                        marginBottom: "20px",
                      }}
                    >
                      From the homepage click on a project, on the plot
                      selection page click "Sub-codon position profile" in the
                      top left. You will see the page shown in the screenshot
                      below:
                    </p>

                    <div className="text-center mb-4">
                      <img
                        src={transcriptEntryImg}
                        alt="Transcript Entry Screenshot"
                        style={{
                          width: "80%",
                          maxWidth: "800px",
                          height: "auto",
                          borderRadius: "8px",
                          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                          border: "1px solid #e5e7eb",
                        }}
                      />
                    </div>

                    <p
                      style={{
                        fontSize: "15px",
                        lineHeight: "1.6",
                        color: "#374151",
                        marginBottom: "20px",
                      }}
                    >
                      Enter a gene name or transcript id in the Gene/Transcript
                      input box. If you enter a gene name a list of transcripts
                      for that gene will pop up, select the one that says
                      principal in the last column and press Submit. Leave the
                      other settings at their default values, you can read more
                      about them by hovering over the information icon (ⓘ) next
                      to each option, a pop up will describe what the option
                      does.
                    </p>

                    <p
                      style={{
                        fontSize: "15px",
                        lineHeight: "1.6",
                        color: "#374151",
                        marginBottom: "20px",
                      }}
                    >
                      Click the "View plot" button at the bottom of the page, or
                      press the Enter button on your keyboard.
                    </p>

                    <div className="text-center mb-4">
                      <img
                        src={annotatedSubcodonImg}
                        alt="Annotated Subcodon Screenshot"
                        style={{
                          width: "80%",
                          maxWidth: "800px",
                          height: "auto",
                          borderRadius: "8px",
                          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                          border: "1px solid #e5e7eb",
                        }}
                      />
                    </div>

                    <p
                      style={{
                        fontSize: "15px",
                        lineHeight: "1.6",
                        color: "#374151",
                        marginBottom: "20px",
                      }}
                    >
                      You will see a plot similiar to the screenshot above. In
                      the main part of the plot inferred A-sites of Ribo-Seq
                      reads from the selected project are displayed. These reads
                      are coloured red/green/blue. These correspond to the open
                      reading frame architecture shown beneath the plot, where
                      each of the three reading frames are represented by a
                      horizontal bar also coloured red/green/blue. AUG codons
                      are denoted by short white lines, while stop codons are
                      longer black lines.
                    </p>

                    <p
                      style={{
                        fontSize: "15px",
                        lineHeight: "1.6",
                        color: "#374151",
                        marginBottom: "20px",
                      }}
                    >
                      The annotated CDS start and CDS stop are denoted by
                      vertical black lines on the main plot and in this example
                      correspond to a start/stop codon in the third reading
                      frame beneath the plot (blue). As expected Ribo-Seq reads
                      are mostly contained between the CDS start and CDS stop
                      and biased toward the third reading frame (blue). This
                      bias will be more obvious the when the periodicity of the
                      data you are viewing is stronger.
                    </p>

                    <p
                      style={{
                        fontSize: "15px",
                        lineHeight: "1.6",
                        color: "#374151",
                        marginBottom: "0",
                      }}
                    >
                      The sub-codon position profile page is useful for
                      exploring specific cases when you have a gene/transcript
                      in mind that you want to explore. If you don't have a
                      specific gene/transcript in mind and are looking
                      specifically for cases of translated uORFs or other ORF
                      types then see the Translon Explorer page.
                    </p>
                  </div>
                </div>

                {/* Section 2: Downloading raw counts */}
                <div className="mb-5">
                  <h5
                    className="mb-4 fw-bold"
                    style={{
                      color: "#2F9E37",
                      fontSize: "20px",
                      borderBottom: "2px solid #2F9E37",
                      paddingBottom: "8px",
                    }}
                  >
                    Downloading raw counts
                  </h5>

                  <div className="mb-4">
                    <p
                      style={{
                        fontSize: "15px",
                        lineHeight: "1.6",
                        color: "#374151",
                        marginBottom: "20px",
                      }}
                    >
                      From the homepage click on a project, on the plot
                      selection page click "Data quality". 
                      The top of the page will have a list of a number of different plot
                      types, click on 'Counts table'
                    </p>

                    <p
                      style={{
                        fontSize: "15px",
                        lineHeight: "1.6",
                        color: "#374151",
                        marginBottom: "20px",
                      }}
                    >
                      In the 'Plot Options' section beneath the plot types, there is
                      options to select gene or transcript level counts, the count type,  
                      whether to aggregate results and a text input box if you want counts
                      for specific genes/transcripts.
                    </p>

                    <p
                      style={{
                        fontSize: "15px",
                        lineHeight: "1.6",
                        color: "#374151",
                        marginBottom: "20px",
                      }}
                    >
                      Select one or more files in the 'Select files' section 
                      and click the "Generate table" button at the bottom of 
                      the page, or press the Enter button on your keyboard.
                    </p>

                    <p
                      style={{
                        fontSize: "15px",
                        lineHeight: "1.6",
                        color: "#374151",
                        marginBottom: "20px",
                      }}
                    >
                      An interactive table will show at the bottom of the page showing up
                      to 1000 genes/transcripts with the highest CDS counts. You can 
                      download the entire csv by clicking the 'Download CSV' link at the 
                      top left of the table. To see a more in depth description of the 
                      counts table see the  <a href="/help/plot/data-quality">
                      data quality help page</a>
                    </p>

                  </div>
                </div>

                {/* Section 3: Detecting translated ORFs */}
                <div className="mb-5">
                  <h5
                    className="mb-4 fw-bold"
                    style={{
                      color: "#2F9E37",
                      fontSize: "20px",
                      borderBottom: "2px solid #2F9E37",
                      paddingBottom: "8px",
                    }}
                  >
                    Detecting translons/translated ORFs
                  </h5>

                  <div className="mb-4">
                    <p
                      style={{
                        fontSize: "15px",
                        lineHeight: "1.6",
                        color: "#374151",
                        marginBottom: "20px",
                      }}
                    >
                      From the homepage click on a project id, then Translon
                      Explorer On that page you will various settings
                      broken into the categories ORF Type, Features and Filters.
                      Leave these settings as default, for detailed information
                      on these hover over the information icon (ⓘ) next to each
                      option, a pop up will describe what the option does.
                      Scroll down to the sequence types section. Click on one or
                      more Ribo-Seq files, the circle next to the file name will
                      be filled in to show it is selected. Then click the
                      Generate Table button at the bottom of the page or press
                      the Enter button on your keyboard.
                    </p>

                    <p
                      style={{
                        fontSize: "15px",
                        lineHeight: "1.6",
                        color: "#374151",
                        marginBottom: "20px",
                      }}
                    >
                      After a short while (Note: Wait time will increase when
                      more data is selected, and for organisms with more
                      annotated transcripts) a table will be shown as in the
                      screenshot below:
                    </p>

                    <div className="text-center mb-4">
                      <img
                        src={orfTranslationTableImg}
                        alt="ORF Translation Table Screenshot"
                        style={{
                          width: "80%",
                          maxWidth: "800px",
                          height: "auto",
                          borderRadius: "8px",
                          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                          border: "1px solid #e5e7eb",
                        }}
                      />
                    </div>

                    <p
                      style={{
                        fontSize: "15px",
                        lineHeight: "1.6",
                        color: "#374151",
                        marginBottom: "20px",
                      }}
                    >
                      Each row in the table corresponds to a different ORF,
                      information shown for each ORF includes the start and stop
                      position, the length of the transcript id and the gene
                      name. Each row also includes a link to the sub-codon
                      position profile page. Clicking on the link will open a
                      separate tab where the transcript will be shown with the
                      selected data already loaded and the ORF will be
                      highligted in yellow. Clicking on the arrow on the bottom
                      right of the table will show the next page of the table.
                      This table can show up to 1000 ORFs, in cases where there
                      are more than 1000 ORFs a csv file with all the ORFs can
                      be downloaded via a link at the top left of the table.
                    </p>

                    <p
                      style={{
                        fontSize: "15px",
                        lineHeight: "1.6",
                        color: "#374151",
                        marginBottom: "0",
                      }}
                    >
                      The above can be repeated for different ORF types which
                      can be selected at the top left of the page. Note however
                      that ORF detection works best with studies that have
                      strong triplet periodicity. This can be checked on the
                      data quality page.
                    </p>
                  </div>
                </div>

                {/* Section 4: Data quality */}
                <div className="mb-5">
                  <h5
                    className="mb-4 fw-bold"
                    style={{
                      color: "#2F9E37",
                      fontSize: "20px",
                      borderBottom: "2px solid #2F9E37",
                      paddingBottom: "8px",
                    }}
                  >
                    Data quality
                  </h5>

                  <div className="mb-4">
                    <p
                      style={{
                        fontSize: "15px",
                        lineHeight: "1.6",
                        color: "#374151",
                        marginBottom: "20px",
                      }}
                    >
                      From the homepage click on a project id, then Data
                      quality. At the top of the page you'll see various
                      different plot types. Click on the triplet periodicity
                      plot. Beneath the plot selection is a section for options
                      which will change depending on the chosen plot type. Leave
                      them on default for now, again you can hover over the
                      information icon next to each option to see what it does.
                      Scroll down and click on one or more Ribo-Seq or RNA-Seq
                      files, then click view plot at the bottom of the page, or
                      click the Enter button on your keyboard. The image below
                      shows some of the plots that can be generated from this
                      page:
                    </p>

                    <div className="text-center mb-4">
                      <img
                        src={dataQualityPanelImg}
                        alt="Data Quality Panel Screenshot"
                        style={{
                          width: "80%",
                          maxWidth: "800px",
                          height: "auto",
                          borderRadius: "8px",
                          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                          border: "1px solid #e5e7eb",
                        }}
                      />
                    </div>

                    <p
                      style={{
                        fontSize: "15px",
                        lineHeight: "1.6",
                        color: "#374151",
                        marginBottom: "20px",
                      }}
                    >
                      The top left plot in this image shows the triplet
                      periodicity of the selected data. Read lengths are shown
                      on the x-axis while the count is shown on the y-axis. Each
                      read length has three bars corresponding to each of the
                      three reading frames. If a dataset has strong periodicity
                      one of the reading frames will be much higher than the
                      other two. A score is also shown at the top right of the
                      plot between 0 and 1, with 0 being the worst periodicity
                      and 1 being the best. The better the periodicity the
                      easier it will be to detect ORFs and the clearer it will
                      be when visualising sub-codon profiles.
                    </p>

                    <p
                      style={{
                        fontSize: "15px",
                        lineHeight: "1.6",
                        color: "#374151",
                        marginBottom: "0",
                      }}
                    >
                      Other plot types on this page can be used to check other
                      aspects of datasets such as the read length distribution
                      or metagene profiles, which can be useful when assessing a
                      dataset's quality. A description of each can be seen by
                      hovering over the information icon next to each plot's
                      name.
                    </p>
                  </div>
                </div>

                {/* Section 5: Differential expression/translation */}
                <div className="mb-5">
                  <h5
                    className="mb-4 fw-bold"
                    style={{
                      color: "#2F9E37",
                      fontSize: "20px",
                      borderBottom: "2px solid #2F9E37",
                      paddingBottom: "8px",
                    }}
                  >
                    Differential expression/translation
                  </h5>

                  <div className="mb-4">
                    <p
                      style={{
                        fontSize: "15px",
                        lineHeight: "1.6",
                        color: "#374151",
                        marginBottom: "20px",
                      }}
                    >
                      From the homepage click on a project id, then Differential
                      plot. Leave the settings at the top of the page at their
                      defaults. The file selection here works differently than
                      the sub-codon position profiles or data quality pages.
                    </p>

                    <div className="text-center mb-4">
                      <img
                        src={deFileSelectionImg}
                        alt="DE File Selection Screenshot"
                        style={{
                          width: "80%",
                          maxWidth: "800px",
                          height: "auto",
                          borderRadius: "8px",
                          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                          border: "1px solid #e5e7eb",
                        }}
                      />
                    </div>

                    <p
                      style={{
                        fontSize: "15px",
                        lineHeight: "1.6",
                        color: "#374151",
                        marginBottom: "20px",
                      }}
                    >
                      First click on a file to select it, then drag it into the
                      "Ribo-Seq files Condition 1" box below the files section.
                      Do the same for the "Ribo-Seq files Condition 2" box. It
                      is recommended to have at least 2 files in each box and an
                      equal number of files in each, though unequal file numbers
                      are tolerated.
                    </p>

                    <div className="text-center mb-4">
                      <img
                        src={deFileSelectionRnaseqImg}
                        alt="DE File Selection RNA-seq Screenshot"
                        style={{
                          width: "80%",
                          maxWidth: "800px",
                          height: "auto",
                          borderRadius: "8px",
                          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                          border: "1px solid #e5e7eb",
                        }}
                      />
                    </div>

                    <p
                      style={{
                        fontSize: "15px",
                        lineHeight: "1.6",
                        color: "#374151",
                        marginBottom: "20px",
                      }}
                    >
                      Click on the mRNA-Seq button in the 'Seq-Type' section and
                      repeat the process for RNA-Seq files.
                    </p>

                    <p
                      style={{
                        fontSize: "15px",
                        lineHeight: "1.6",
                        color: "#374151",
                        marginBottom: "20px",
                      }}
                    >
                      Then click the View Plot button at the bottom of the page.
                      You should see a plot like the following:
                    </p>

                    <div className="text-center mb-4">
                      <img
                        src={dePlotImg}
                        alt="DE Plot Screenshot"
                        style={{
                          width: "80%",
                          maxWidth: "800px",
                          height: "auto",
                          borderRadius: "8px",
                          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                          border: "1px solid #e5e7eb",
                        }}
                      />
                    </div>

                    <p
                      style={{
                        fontSize: "15px",
                        lineHeight: "1.6",
                        color: "#374151",
                        marginBottom: "20px",
                      }}
                    >
                      This plot shows the Ribo-Seq/RNA-Seq fold change of each
                      gene in the selected files as calculated by deltaTE.
                      Hovering over a specific point tells you the
                      gene/transcript id of the point as well as the specific
                      fold change. Clicking on a point will open a seprate tab
                      showing a comparison plot of the Ribo-Seq and mRNA-Seq for
                      each condition.
                    </p>

                    <p
                      style={{
                        fontSize: "15px",
                        lineHeight: "1.6",
                        color: "#374151",
                        marginBottom: "20px",
                      }}
                    >
                      Ribo-Seq reads are shown in dark green/red corresponding
                      to condition 1/2, while mRNA-Seq reads are shown in light
                      green/red corresponding to condition 1/2. These colours
                      can be changed according to preference.
                    </p>

                    <p
                      style={{
                        fontSize: "15px",
                        lineHeight: "1.6",
                        color: "#374151",
                        marginBottom: "0",
                      }}
                    >
                      Comparison plots can also be generated manually by going
                      to the comparison plot page directly. Up to 8 conditions
                      can be plotted there which makes it useful for looking at
                      variation between replicates or comparing more than two
                      conditions. File selection on the comparison plot page
                      uses the same drag and drop method as on the differential
                      plot page, see the tutorial video for more information.
                    </p>
                  </div>
                </div>

                {/* Back to Help Button */}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default QuickStart;
